/**
 * Vendor sub-pricing APIs (backend contract summary):
 *
 * Summary — `GET service-contract/sub-vendor-contract/pricing-summary/`
 *   Query: `vendor_id` (required), optional `sub_vendor_user_id`, optional `apartment_id`.
 *   First load may send only `vendor_id`; backend returns `crew`, `defaults`, workspace/service
 *   lists. When sub and optionally apartment are provided, sidebar rows include per-slice flags.
 *
 * Detail — `GET service-contract/sub-vendor-contract/pricing-detail/`
 *   Query: `vendor_id`, `sub_vendor_user_id`, `apartment_id` (all required).
 *
 * Save lines — `POST service-contract/sub-vendor-contract/pricing-lines/`
 *   Body: `VendorSubPricingSaveLinesRequest` (vendor, sub, apartment, service ids + sub-contract lines).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import type {
    VendorSubPricingDetailParams,
    VendorSubPricingDetailResponse,
    VendorSubPricingSaveLinesRequest,
    VendorSubPricingSummaryParams,
    VendorSubPricingSummaryResponse,
} from '../models/VendorSubPricing/types'

export const vendorSubPricingSummaryQueryKeyPrefix = [
    'vendorSubPricingSummary',
] as const
export const vendorSubPricingDetailQueryKeyPrefix = [
    'vendorSubPricingDetail',
] as const

export function getVendorSubPricingErrorMessage(e: unknown): string {
    const err = e as AxiosError<{ error?: string; message?: string }>
    const data = err.response?.data
    const fromBody =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message)
    if (fromBody) return fromBody
    if (err.response?.status === 403) {
        return 'You do not have access to this pricing data.'
    }
    if (err.response?.status === 404) {
        return 'Team member or property not found.'
    }
    if (err.response?.status === 400) {
        return 'Invalid pricing request.'
    }
    return err.message ?? 'Could not load pricing.'
}

const SUMMARY_PATH = 'service-contract/sub-vendor-contract/pricing-summary/'
const DETAIL_PATH = 'service-contract/sub-vendor-contract/pricing-detail/'
const SAVE_LINES_PATH = 'service-contract/sub-vendor-contract/pricing-lines/'

/** Silent GET for pricing-detail — use with `queryClient.fetchQuery` to share cache with `useVendorSubPricingDetail`. */
export async function fetchVendorSubPricingDetail(
    params: VendorSubPricingDetailParams,
    signal?: AbortSignal,
): Promise<VendorSubPricingDetailResponse> {
    const res = await axiosInstance.get<VendorSubPricingDetailResponse>(
        DETAIL_PATH,
        {
            params: {
                vendor_id: params.vendor_id,
                sub_vendor_user_id: params.sub_vendor_user_id,
                apartment_id: params.apartment_id,
            },
            signal,
        },
    )
    return res.data
}

function buildSummaryRequestParams(params: VendorSubPricingSummaryParams) {
    const q: Record<string, number> = { vendor_id: params.vendor_id }
    if (params.sub_vendor_user_id != null) {
        q.sub_vendor_user_id = params.sub_vendor_user_id
    }
    if (params.apartment_id != null) {
        q.apartment_id = params.apartment_id
    }
    return q
}

export interface UseVendorSubPricingSummaryArgs {
    vendor_id: number | null
    sub_vendor_user_id?: number | null
    apartment_id?: number | null
}

/** Summary for left pane — enabled when logged-in vendor id is known. */
export function useVendorSubPricingSummary(
    args: UseVendorSubPricingSummaryArgs,
) {
    const { vendor_id, sub_vendor_user_id, apartment_id } = args

    return useQuery<VendorSubPricingSummaryResponse, Error>({
        queryKey: [
            ...vendorSubPricingSummaryQueryKeyPrefix,
            vendor_id,
            sub_vendor_user_id ?? null,
            apartment_id ?? null,
        ],
        queryFn: async ({ signal }) => {
            const res = await axiosInstance.get<VendorSubPricingSummaryResponse>(
                SUMMARY_PATH,
                {
                    params: buildSummaryRequestParams({
                        vendor_id: vendor_id!,
                        sub_vendor_user_id: sub_vendor_user_id ?? undefined,
                        apartment_id: apartment_id ?? undefined,
                    }),
                    signal,
                },
            )
            return res.data
        },
        enabled: vendor_id != null && Number.isFinite(vendor_id),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        onError: (error) => toast.error(getVendorSubPricingErrorMessage(error)),
    })
}

export interface UseVendorSubPricingDetailArgs {
    vendor_id: number | null
    sub_vendor_user_id: number | null
    apartment_id: number | null
}

/** Full matrix slice — loads when workspace + sub + vendor are chosen. */
export function useVendorSubPricingDetail(args: UseVendorSubPricingDetailArgs) {
    const { vendor_id, sub_vendor_user_id, apartment_id } = args

    const requested: VendorSubPricingDetailParams | null =
        vendor_id != null &&
        sub_vendor_user_id != null &&
        apartment_id != null &&
        Number.isFinite(vendor_id) &&
        Number.isFinite(sub_vendor_user_id) &&
        Number.isFinite(apartment_id)
            ? {
                  vendor_id,
                  sub_vendor_user_id,
                  apartment_id,
              }
            : null

    return useQuery<VendorSubPricingDetailResponse, Error>({
        queryKey: requested
            ? [
                  ...vendorSubPricingDetailQueryKeyPrefix,
                  requested.vendor_id,
                  requested.sub_vendor_user_id,
                  requested.apartment_id,
              ]
            : [...vendorSubPricingDetailQueryKeyPrefix, null, null, null],
        queryFn: async ({ signal }) =>
            fetchVendorSubPricingDetail(requested!, signal),
        enabled: requested != null,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        onError: (error) => toast.error(getVendorSubPricingErrorMessage(error)),
    })
}

/** Bulk upsert vendor→crew sub-contracts for one service/workspace/sub slice; invalidates detail + summary caches. */
export function useVendorSubPricingSaveLines() {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, VendorSubPricingSaveLinesRequest>({
        mutationFn: async (body) => {
            await axiosInstance.post(SAVE_LINES_PATH, body)
        },
        onSuccess: (_data, variables) => {
            const { vendor_id, sub_vendor_user_id, apartment_id } = variables
            queryClient.invalidateQueries({
                queryKey: [
                    ...vendorSubPricingDetailQueryKeyPrefix,
                    vendor_id,
                    sub_vendor_user_id,
                    apartment_id,
                ],
            })
            queryClient.invalidateQueries({
                queryKey: [...vendorSubPricingSummaryQueryKeyPrefix, vendor_id],
            })
            toast.success('Sub-contracts saved')
        },
        onError: (error) => toast.error(getVendorSubPricingErrorMessage(error)),
    })
}
